import { useEffect } from 'react';
import i18next from 'i18next';

export interface DocumentMetaAlternate {
  hrefLang: string;
  href: string;
}

export interface DocumentMeta {
  title: string;
  description?: string;
  /** Absolute or root-relative path; resolved against the current origin. */
  canonicalPath?: string;
  /** BCP 47 tag written to `<html lang>` while this page is mounted. */
  lang?: string;
  ogType?: string;
  ogImagePath?: string;
  ogImageAlt?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  alternates?: DocumentMetaAlternate[];
}

type Cleanup = () => void;

function absoluteUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return new URL(path, window.location.origin).toString();
}

function upsertMeta(selectorAttr: 'name' | 'property', key: string, content: string): Cleanup {
  const selector = `meta[${selectorAttr}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (existing) {
    const previous = existing.getAttribute('content');
    existing.setAttribute('content', content);
    return () => {
      if (previous === null) existing.removeAttribute('content');
      else existing.setAttribute('content', previous);
    };
  }

  const created = document.createElement('meta');
  created.setAttribute(selectorAttr, key);
  created.setAttribute('content', content);
  document.head.appendChild(created);
  return () => created.remove();
}

function appendLink(rel: string, href: string, hrefLang?: string): Cleanup {
  const created = document.createElement('link');
  created.setAttribute('rel', rel);
  created.setAttribute('href', href);
  if (hrefLang) created.setAttribute('hreflang', hrefLang);
  document.head.appendChild(created);
  return () => created.remove();
}

/**
 * Sets document title and social/SEO head tags for the lifetime of a route, restoring
 * whatever was there before on unmount. Deliberately dependency-free — a single public
 * page does not justify a head-manager provider at the app root.
 */
export function useDocumentMeta(meta: DocumentMeta): void {
  const {
    title,
    description,
    canonicalPath,
    lang,
    ogType = 'website',
    ogImagePath,
    ogImageAlt,
    twitterCard = 'summary_large_image',
    alternates,
  } = meta;

  const alternatesKey = JSON.stringify(alternates ?? []);

  useEffect(() => {
    const cleanups: Cleanup[] = [];

    const previousTitle = document.title;
    document.title = title;
    cleanups.push(() => {
      document.title = previousTitle;
    });

    if (lang) {
      document.documentElement.lang = lang;
      // i18n owns <html lang> (lib/i18n.ts). Hand back to the active language, not
      // the value captured at mount: the visitor may have switched language here.
      cleanups.push(() => {
        if (i18next.language) document.documentElement.lang = i18next.language;
      });
    }

    cleanups.push(upsertMeta('property', 'og:title', title));
    cleanups.push(upsertMeta('property', 'og:type', ogType));
    cleanups.push(upsertMeta('name', 'twitter:card', twitterCard));
    cleanups.push(upsertMeta('name', 'twitter:title', title));

    if (description) {
      cleanups.push(upsertMeta('name', 'description', description));
      cleanups.push(upsertMeta('property', 'og:description', description));
      cleanups.push(upsertMeta('name', 'twitter:description', description));
    }

    if (canonicalPath) {
      const canonical = absoluteUrl(canonicalPath);
      cleanups.push(upsertMeta('property', 'og:url', canonical));
      cleanups.push(appendLink('canonical', canonical));
    }

    if (ogImagePath) {
      const image = absoluteUrl(ogImagePath);
      cleanups.push(upsertMeta('property', 'og:image', image));
      cleanups.push(upsertMeta('name', 'twitter:image', image));
      if (ogImageAlt) {
        cleanups.push(upsertMeta('property', 'og:image:alt', ogImageAlt));
        cleanups.push(upsertMeta('name', 'twitter:image:alt', ogImageAlt));
      }
    }

    for (const alternate of (JSON.parse(alternatesKey) as DocumentMetaAlternate[])) {
      cleanups.push(appendLink('alternate', absoluteUrl(alternate.href), alternate.hrefLang));
    }

    return () => {
      // Reverse order so restored attributes win over later overwrites.
      for (const cleanup of cleanups.reverse()) cleanup();
    };
  }, [
    title,
    description,
    canonicalPath,
    lang,
    ogType,
    ogImagePath,
    ogImageAlt,
    twitterCard,
    alternatesKey,
  ]);
}
